import type {
  MaxAmountCurrency,
  MinAmountCurrency,
  PaymentCurrency
} from '../../common';
import {
  isMaxAmountCurrency,
  isMinAmountCurrency,
  isSpecialZeroDecimalCurrency,
  isZeroDecimalCurrency
} from '../../common';

export const AmountConversionErrors = {
  InvalidNumber: 'InvalidNumber',
  InsufficientAmount: 'InsufficientAmount',
  ExceedingAmount: 'ExceedingAmount'
} as const;

type AmountConversionError =
  (typeof AmountConversionErrors)[keyof typeof AmountConversionErrors];

export function isValidPaymentAmount({
  amount,
  currency
}: {
  amount: number;
  currency: PaymentCurrency;
}): [true, undefined] | [false, AmountConversionError] {
  if (isNaN(amount)) {
    return [false, AmountConversionErrors.InvalidNumber];
  }

  const min = getMinAmount(currency);
  const max = getMaxAmount(currency);

  if (amount < min) {
    return [false, AmountConversionErrors.InsufficientAmount];
  }

  if (amount > max) {
    return [false, AmountConversionErrors.ExceedingAmount];
  }

  return [true, undefined];
}

export function toStripeAmount({
  amount,
  currency
}: {
  amount: number;
  currency: PaymentCurrency;
}) {
  if (amount === 0) {
    return 0;
  }

  if (isSpecialZeroDecimalCurrency(currency)) {
    return Math.round(amount) * 100;
  }

  if (isZeroDecimalCurrency(currency)) {
    return Math.round(amount);
  }

  return Math.round(amount * 100);
}

// используется чтобы отображать в виджете в точности ту цену,
// которая будет испольоваться для оплаты
export function toStripeDisplayAmount({
  amount,
  currency
}: {
  amount: number;
  currency: PaymentCurrency;
}) {
  if (amount === 0) {
    return 0;
  }

  if (
    isZeroDecimalCurrency(currency) ||
    isSpecialZeroDecimalCurrency(currency)
  ) {
    return Math.round(amount);
  }

  return Math.round(amount * 100) / 100;
}

export function fromStripeAmount({
  amount,
  currency
}: {
  amount: number;
  currency: PaymentCurrency;
}) {
  if (isZeroDecimalCurrency(currency)) {
    return amount;
  }

  return amount / 100;
}

function getMinAmount(currency: PaymentCurrency) {
  if (isMinAmountCurrency(currency)) {
    return minAmounts[currency];
  }

  return minAmounts.other;
}

function getMaxAmount(currency: PaymentCurrency) {
  if (isMaxAmountCurrency(currency)) {
    return maxAmounts[currency];
  }

  return maxAmounts.other;
}

const minAmounts: Record<MinAmountCurrency | 'other', number> = {
  USD: 0.5,
  AED: 2.0,
  AUD: 0.5,
  BGN: 1.0,
  BRL: 0.5,
  CAD: 0.5,
  CHF: 0.5,
  CZK: 15,
  DKK: 2.5,
  EUR: 0.5,
  GBP: 0.3,
  HKD: 4.0,
  HUF: 175,
  INR: 0.5,
  JPY: 50,
  MXN: 10,
  MYR: 2,
  NOK: 3,
  NZD: 0.5,
  PLN: 2,
  RON: 2,
  SEK: 3,
  SGD: 0.5,
  THB: 10,
  other: 0
};

const maxAmounts: Record<MaxAmountCurrency | 'other', number> = {
  IDR: 9999999999.99, // 12 digits
  INR: 9999999.99, // 9 digits
  LBP: 999999999999, // 11 digits
  COP: 9999999999.9, // 10 digits
  HUF: 9999999999, // 10 digits
  JPY: 9999999999, // 10 digits
  other: 999999.99 // 8 digits
};
